import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AdminUsersSection, AdminAuditSection } from "./AdminUsersSection";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AdminSettings() {
  const { user } = useAuth();
  const isManager = ["admin", "super_admin"].includes(user?.role);
  const [form, setForm] = useState(null);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
    api.get("/admin/private-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/admin/contact-messages").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { key, ...payload } = form;
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés — le site est mis à jour");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  if (!form) return <p className="text-gray-500">Chargement…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setHour = (day) => (e) => setForm({ ...form, opening_hours: { ...form.opening_hours, [day]: e.target.value } });

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif-elegant text-3xl font-bold mb-8">Paramètres du restaurant</h1>
      <form onSubmit={save} className="space-y-8 max-w-3xl">
        <div className="ic-card !transform-none p-6 space-y-4">
          <h2 className="gold-label">Informations générales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="settings-name" className="ic-input" placeholder="Nom" value={form.name || ""} onChange={set("name")} />
            <input data-testid="settings-phone" className="ic-input" placeholder="Téléphone" value={form.phone || ""} onChange={set("phone")} />
            <input data-testid="settings-address" className="ic-input sm:col-span-2" placeholder="Adresse" value={form.address || ""} onChange={set("address")} />
            <input data-testid="settings-whatsapp" className="ic-input" placeholder="WhatsApp (+33…)" value={form.whatsapp || ""} onChange={set("whatsapp")} />
            <input data-testid="settings-email" className="ic-input" placeholder="Email" value={form.email || ""} onChange={set("email")} />
            <input data-testid="settings-maps" className="ic-input sm:col-span-2" placeholder="Lien Google Maps" value={form.maps_url || ""} onChange={set("maps_url")} />
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 block mb-1">🎂 Bonus RITAS d'anniversaire (crédité automatiquement le jour de l'anniversaire du client)</label>
              <input data-testid="settings-birthday-bonus" type="number" min="0" className="ic-input !w-40" value={form.birthday_bonus ?? 50} onChange={(e) => setForm({ ...form, birthday_bonus: Number(e.target.value) })} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 block mb-1">🎉 Bonus RITAS de bienvenue (crédité à la première réservation confirmée d'un client inscrit)</label>
              <input data-testid="settings-first-reservation-bonus" type="number" min="0" className="ic-input !w-40" value={form.first_reservation_bonus ?? 50} onChange={(e) => setForm({ ...form, first_reservation_bonus: Number(e.target.value) })} />
            </div>
          </div>
        </div>
        <div className="ic-card !transform-none p-6 space-y-4">
          <h2 className="gold-label">Textes du site</h2>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Sous-titre de la page d'accueil</label>
            <input data-testid="settings-hero-subtitle" className="ic-input" value={form.hero_subtitle || ""} onChange={set("hero_subtitle")} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Texte « À propos »</label>
            <textarea data-testid="settings-about" rows="4" className="ic-input" value={form.about_text || ""} onChange={set("about_text")} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Texte « Terrasse &amp; Événements privés »</label>
            <textarea data-testid="settings-terrace" rows="4" className="ic-input" value={form.terrace_text || ""} onChange={set("terrace_text")} />
          </div>
        </div>
        <div className="ic-card !transform-none p-6 space-y-3">
          <h2 className="gold-label">Horaires d'ouverture</h2>
          {DAYS.map((day) => (
            <div key={day} className="flex items-center gap-4">
              <span className="w-24 text-sm text-gray-300">{day}</span>
              <input data-testid={`settings-hours-${day.toLowerCase()}`} className="ic-input flex-1" value={form.opening_hours?.[day] || ""} onChange={setHour(day)} />
            </div>
          ))}
        </div>
        <button type="submit" data-testid="settings-save-btn" className="btn-gold">Enregistrer les paramètres</button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-14">
        <div>
          <h2 className="font-serif-elegant text-xl font-semibold mb-4">Demandes d'événements privés</h2>
          <div className="space-y-3">
            {requests.length === 0 && <p className="text-gray-500 text-sm">Aucune demande.</p>}
            {requests.map((r) => (
              <div key={r.id} className="ic-card !transform-none p-4 text-sm" data-testid={`private-request-${r.id}`}>
                <p className="font-medium">{r.name} — {r.event_type}</p>
                <p className="text-gray-400 mt-1">{r.email} · {r.phone}{r.guests ? ` · ${r.guests} invités` : ""}{r.date ? ` · ${r.date}` : ""}</p>
                {r.message && <p className="text-gray-500 text-xs mt-1">{r.message}</p>}
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-serif-elegant text-xl font-semibold mb-4">Messages de contact</h2>
          <div className="space-y-3">
            {messages.length === 0 && <p className="text-gray-500 text-sm">Aucun message.</p>}
            {messages.map((m) => (
              <div key={m.id} className="ic-card !transform-none p-4 text-sm">
                <p className="font-medium">{m.name}</p>
                <p className="text-gray-400 text-xs mt-0.5">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                <p className="text-gray-300 mt-2">{m.message}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isManager && <AdminUsersSection />}
      {isManager && <AdminAuditSection />}
    </div>
  );
}
